import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, startOfWeek, endOfWeek, addDays, isSameDay, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { getErrorMessage, isReasonRequiredError } from '../lib/errorMessage';
import { formatHoursToHM } from '../lib/formatTime';
import {
  Clock,
  Plus,
  Trash2,
  Edit2,
  ChevronLeft,
  ChevronRight,
  Send,
  Check,
  X,
  Calendar as CalendarIcon,
  Briefcase,
  FolderKanban,
  AlertTriangle,
  ShieldAlert,
  Shield,
  History,
  Lock,
  Unlock,
  Undo2,
  Info,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { GoBDDeleteConfirmDialog, useGoBDDelete } from '../components/GoBDDeleteConfirmDialog';
import { AuditHistoryPanel } from '../components/AuditHistoryPanel';
import { LockBadge, LockStatusIndicator, UnlockHintBanner, StornoBadge } from '../components/LockBadge';
import { UnlockRequestDialog } from '../components/UnlockRequestDialog';
import { CorrectionDialog } from '../components/CorrectionDialog';
import { FixedWeekBanner } from '../components/DeputyAndFixingComponents';
import LiveRateDisplay from '../components/LiveRateDisplay';
import RestPeriodWarning, { RestPeriodIndicator } from '../components/RestPeriodWarning';
import RestPeriodViolationsList from '../components/RestPeriodViolationsList';
import AverageWorkTimeStatus, { AverageImpactWarning } from '../components/AverageWorkTimeStatus';
import { SundayWorkHint, SundayWorkIndicator } from '../components/SundayComplianceComponents';
import { SearchableSelect } from '../components/SearchableSelect';
import { CategorySelector, CategoryTag, BillableIndicator } from '../components/CategorySelector';

export default function TimeTrackingPage() {
  const { user, api } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Initialize currentDate from query parameter or current date
  const [currentDate, setCurrentDate] = useState(() => {
    const weekParam = searchParams.get('week');
    if (weekParam) {
      try {
        const parsedDate = parseISO(weekParam);
        if (!isNaN(parsedDate.getTime())) {
          // Normalize to Monday of that week
          return startOfWeek(parsedDate, { weekStartsOn: 1 });
        }
      } catch (e) {
        // Invalid date, fall through to default
      }
    }
    return new Date();
  });
  const [weekData, setWeekData] = useState({ work_time: [], project_time: [], approval: null, holidays: {}, vacations: [] });
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Dialog states
  const [workTimeDialogOpen, setWorkTimeDialogOpen] = useState(false);
  const [projectTimeDialogOpen, setProjectTimeDialogOpen] = useState(false);
  const [editingWorkTime, setEditingWorkTime] = useState(null);
  const [editingProjectTime, setEditingProjectTime] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  
  // Dispute dialog state
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  
  // Withdraw confirmation dialog state
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  
  // GoBD Delete Dialog hook
  const { 
    dialogOpen: deleteDialogOpen, 
    setDialogOpen: setDeleteDialogOpen, 
    deleteConfig, 
    requestDelete, 
    handleConfirm: handleDeleteConfirm, 
    handleCancel: handleDeleteCancel 
  } = useGoBDDelete();
  
  // Audit History state
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [historyEntry, setHistoryEntry] = useState({ collection: '', id: '' });

  // Lock status state
  const [lockStatuses, setLockStatuses] = useState({});
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [unlockEntries, setUnlockEntries] = useState([]);
  const [unlockCollection, setUnlockCollection] = useState('work_time');
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [correctionEntry, setCorrectionEntry] = useState(null);
  const [correctionCollection, setCorrectionCollection] = useState('work_time');
  
  // Week status state (from /locks/week-status)
  const [weekStatus, setWeekStatus] = useState(null);
  
  // Day unlock dialog state
  const [dayUnlockDialogOpen, setDayUnlockDialogOpen] = useState(false);
  const [dayUnlockDate, setDayUnlockDate] = useState(null);
  const [dayUnlockReason, setDayUnlockReason] = useState('');
  
  // Reason field state for editing
  const [editReason, setEditReason] = useState('');
  const [showReasonField, setShowReasonField] = useState(false);
  
  // Rest period warning state
  const [restPeriodWarning, setRestPeriodWarning] = useState(null);
  
  // Sunday work hint state
  const [sundayWorkHint, setSundayWorkHint] = useState(null);
  
  // Category state
  const [flatCategories, setFlatCategories] = useState([]);
  
  // Review entries banner state
  const [reviewEntries, setReviewEntries] = useState([]);
  
  // Query param for opening specific entry dialog
  const [entryToOpen, setEntryToOpen] = useState(() => searchParams.get('entry'));

  // Form data
  const [workTimeForm, setWorkTimeForm] = useState({
    date: '',
    start_time: '08:00',
    end_time: '16:30',
    break_minutes: 30,
    hours: 8,
    breaks: [{ start_time: '12:00', end_time: '12:30' }],
  });

  // Alteintrag ohne dokumentierte Pausenzeiten
  const [breakEntriesMissing, setBreakEntriesMissing] = useState(false);

  const [projectTimeForm, setProjectTimeForm] = useState({
    project_id: '',
    date: '',
    hours: '',
    description: '',
    is_daily_rate: false,
    category_id: '',
    activity_id: '',
    billable_override: null,
  });
  const [isSavingProjectTime, setIsSavingProjectTime] = useState(false);
  const [categoryValid, setCategoryValid] = useState(true);
  // L1: Tätigkeit – funktionale Aktualisierung, damit gleichzeitige Kategorie-Änderungen nichts überschreiben
  const handleActivityChange = useCallback((id) => {
    setProjectTimeForm((prev) => ({ ...prev, activity_id: id || '' }));
  }, []);

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekStartStr = format(weekStart, 'yyyy-MM-dd');

  const fetchWeekData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [weekRes, projectsRes, customersRes, categoriesRes] = await Promise.all([
        api.get(`/time-entries/week/${weekStartStr}`),
        api.get('/projects'),
        api.get('/customers'),
        api.get('/service-categories/flat?include_inactive=true'),
      ]);
      setWeekData(weekRes.data);
      setProjects(projectsRes.data);
      setCustomers(customersRes.data);
      setFlatCategories(categoriesRes.data);
      
      // Load week-status in parallel
      fetchWeekStatus();
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setIsLoading(false);
    }
  }, [api, weekStartStr]);

  // Fetch week status from /locks/week-status
  const fetchWeekStatus = useCallback(async () => {
    try {
      const res = await api.get(`/locks/week-status?week_start=${weekStartStr}`);
      setWeekStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch week status:', error);
      setWeekStatus(null);
    }
  }, [api, weekStartStr]);

  // Fetch lock statuses for all entries
  const fetchLockStatuses = useCallback(async (workTimeEntries, projectTimeEntries) => {
    const workTimeIds = workTimeEntries.map(e => e.id);
    const projectTimeIds = projectTimeEntries.map(e => e.id);
    
    try {
      const results = {};
      
      if (workTimeIds.length > 0) {
        const workRes = await api.post('/locks/status/bulk', {
          collection_name: 'work_time',
          document_ids: workTimeIds,
        });
        Object.entries(workRes.data).forEach(([id, status]) => {
          results[`work_time:${id}`] = status;
        });
      }
      
      if (projectTimeIds.length > 0) {
        const projectRes = await api.post('/locks/status/bulk', {
          collection_name: 'project_time',
          document_ids: projectTimeIds,
        });
        Object.entries(projectRes.data).forEach(([id, status]) => {
          results[`project_time:${id}`] = status;
        });
      }
      
      setLockStatuses(results);
    } catch (error) {
      console.error('Failed to fetch lock statuses:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchWeekData();
  }, [fetchWeekData]);
  
  // Fetch clock status for review entries banner
  const fetchClockStatusForReviews = useCallback(async () => {
    try {
      const res = await api.get('/clock/status');
      setReviewEntries(res.data.review_entries || []);
    } catch (error) {
      console.error('Failed to fetch clock status:', error);
    }
  }, [api]);
  
  // Initial load of review entries
  useEffect(() => {
    fetchClockStatusForReviews();
  }, [fetchClockStatusForReviews]);
  
  // Open entry dialog from ?entry= query parameter (once after week data loads)
  useEffect(() => {
    if (entryToOpen && weekData.work_time?.length > 0 && !isLoading) {
      const entry = weekData.work_time.find(e => e.id === entryToOpen);
      if (entry) {
        const entryDate = parseISO(entry.date);
        openWorkTimeDialog(entryDate, entry);
        setEntryToOpen(null); // Clear to prevent re-opening
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryToOpen, weekData.work_time, isLoading]);

  // Fetch lock statuses when week data changes
  useEffect(() => {
    if (weekData.work_time?.length > 0 || weekData.project_time?.length > 0) {
      fetchLockStatuses(weekData.work_time || [], weekData.project_time || []);
    }
  }, [weekData.work_time, weekData.project_time, fetchLockStatuses]);

  // Helper to get lock status for an entry
  const getLockStatus = (collection, entryId) => {
    return lockStatuses[`${collection}:${entryId}`] || { level: 'open' };
  };

  // Check if entry can be edited (based on lock level and unlock request)
  const canEditEntry = (collection, entry) => {
    const lockInfo = getLockStatus(collection, entry.id);
    if (lockInfo.level === 'open' || lockInfo.level === 'soft') return true;
    if (lockInfo.level === 'hard' && lockInfo.unlock_request?.status === 'approved') return true;
    return false;
  };

  // Open unlock request dialog
  const openUnlockDialog = (entries, collection) => {
    setUnlockEntries(entries);
    setUnlockCollection(collection);
    setUnlockDialogOpen(true);
  };

  // Open correction/storno dialog
  const openCorrectionDialog = (entry, collection) => {
    setCorrectionEntry(entry);
    setCorrectionCollection(collection);
    setCorrectionDialogOpen(true);
  };

  // Buchbare Projektstatus
  const BOOKABLE_STATUS = ['aktiv', 'pausiert'];

  // Filter projects by selected customer AND bookable status
  const filteredProjects = (() => {
    // Basisfilter: nach Kunde und buchbarem Status
    let available = selectedCustomerId
      ? projects.filter(p => p.customer_id === selectedCustomerId)
      : projects;
    
    // Nur buchbare Projekte (aktiv/pausiert; fehlendes status = aktiv)
    available = available.filter(p => BOOKABLE_STATUS.includes(p.status || 'aktiv'));
    
    // Bearbeiten-Fall: Wenn ein Projekt ausgewählt ist, das nicht mehr buchbar ist,
    // trotzdem in die Liste aufnehmen (damit der aktuelle Wert angezeigt wird)
    if (projectTimeForm.project_id) {
      const selectedProject = projects.find(p => p.id === projectTimeForm.project_id);
      if (selectedProject && !available.find(p => p.id === selectedProject.id)) {
        available = [...available, selectedProject];
      }
    }
    
    return available;
  })();

  const handlePrevWeek = () => setCurrentDate(addDays(currentDate, -7));
  const handleNextWeek = () => setCurrentDate(addDays(currentDate, 7));
  const handleThisWeek = () => setCurrentDate(new Date());

  // Calculate hours from start/end time (rounded to 2 decimals)
  // Supports night shift: if end < start, add 24h (1440 mins)
  const calculateHours = (start, end, breakMins) => {
    // Validate inputs - return 0 if empty or invalid HH:MM format
    const timeRegex = /^\d{2}:\d{2}$/;
    if (!start || !end || !timeRegex.test(start) || !timeRegex.test(end)) {
      return 0;
    }
    
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    let endMins = endH * 60 + endM;
    const startMins = startH * 60 + startM;
    
    // If end_time < start_time (night shift), add 24h
    if (endMins < startMins) {
      endMins += 1440; // 24 * 60
    }
    
    // If end == start: return 0
    if (endMins === startMins) {
      return 0;
    }
    
    const totalMins = endMins - startMins - breakMins;
    const hours = Math.max(0, totalMins / 60);
    return Math.round(hours * 100) / 100; // Round to 2 decimal places
  };

  // ===== Pausenzeiten (§ 4 ArbZG) =====
  const MIN_QUALIFYING_BREAK = 15; // Ruhepause zählt erst ab 15 Minuten
  const MAX_STRETCH_MINUTES = 360; // höchstens 6 Stunden am Stück

  const toMinutes = (hhmm) => {
    if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return null;
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
  };

  const fromMinutes = (mins) => {
    const m = ((mins % 1440) + 1440) % 1440;
    return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  };

  // Dauer einer Pause in Minuten; eine Pause vor dem Arbeitsbeginn liegt am Folgetag
  const breakDuration = (br, workStart) => {
    const ws = toMinutes(workStart);
    let bs = toMinutes(br?.start_time);
    let be = toMinutes(br?.end_time);
    if (bs === null || be === null) return 0;
    if (ws !== null && bs < ws) bs += 1440;
    if (ws !== null && be < ws) be += 1440;
    return be > bs ? be - bs : 0;
  };

  const sumBreakMinutes = (breaks, workStart) =>
    (breaks || []).reduce((sum, br) => sum + breakDuration(br, workStart), 0);

  // Pausenliste übernehmen - Pausenminuten und Arbeitszeit ziehen automatisch nach
  const applyBreaks = (form, breaks) => {
    const list = breaks || [];
    const breakMinutes = sumBreakMinutes(list, form.start_time);
    return {
      ...form,
      breaks: list,
      break_minutes: breakMinutes,
      hours: calculateHours(form.start_time, form.end_time, breakMinutes),
    };
  };

  // Vorschlag für eine weitere Pause: 15 Minuten, zwei Stunden nach der letzten Pause
  const suggestNextBreak = (form) => {
    const list = form.breaks || [];
    if (list.length === 0) return { start_time: '12:00', end_time: '12:30' };
    const last = list.reduce((max, br) => Math.max(max, toMinutes(br.end_time) ?? 0), 0);
    return { start_time: fromMinutes(last + 120), end_time: fromMinutes(last + 135) };
  };

  // Vorschau der § 4-Prüfung für diesen Block; maßgeblich bleibt die Tagesprüfung im Backend
  const getBreakRuleHints = (form) => {
    const hints = [];
    const ws = toMinutes(form.start_time);
    let we = toMinutes(form.end_time);
    if (ws === null || we === null) return hints;
    if (we <= ws) we += 1440;

    const all = form.breaks || [];
    const qualifying = all.filter((br) => breakDuration(br, form.start_time) >= MIN_QUALIFYING_BREAK);
    const qualifyingMinutes = qualifying.reduce((sum, br) => sum + breakDuration(br, form.start_time), 0);
    const shortCount = all.length - qualifying.length;
    const hours = Number(form.hours) || 0;
    const required = hours > 9 ? 45 : hours > 6 ? 30 : 0;

    if (required > 0 && qualifyingMinutes < required) {
      hints.push(
        `Bei ${formatHoursToHM(hours)} Arbeitszeit sind ${required} Minuten Ruhepause nötig – nachgewiesen sind ${qualifyingMinutes} Minuten. Es fehlen ${required - qualifyingMinutes} Minuten.`
      );
    }
    if (shortCount > 0) {
      hints.push(
        `${shortCount} Pause(n) unter 15 Minuten werden von der Arbeitszeit abgezogen, erfüllen die Ruhepause nach § 4 ArbZG aber nicht.`
      );
    }

    const cuts = qualifying
      .map((br) => {
        let bs = toMinutes(br.start_time);
        let be = toMinutes(br.end_time);
        if (bs < ws) bs += 1440;
        if (be < ws) be += 1440;
        return [bs, be];
      })
      .sort((a, b) => a[0] - b[0]);
    let cursor = ws;
    let longest = 0;
    cuts.forEach(([bs, be]) => {
      if (bs >= we || be <= cursor) return;
      longest = Math.max(longest, bs - cursor);
      cursor = Math.max(cursor, be);
    });
    longest = Math.max(longest, we - cursor);
    if (longest > MAX_STRETCH_MINUTES) {
      hints.push(
        `${Math.floor(longest / 60)}:${String(longest % 60).padStart(2, '0')} h am Stück ohne Ruhepause – nach § 4 ArbZG sind höchstens sechs Stunden zulässig.`
      );
    }
    return hints;
  };

  // Dokumentierte Pausenzeiten eines Eintrags nachladen
  const loadBreaksForEntry = async (entry) => {
    try {
      const res = await api.get(`/breaks?date=${entry.date}`);
      const rows = (res.data || [])
        .filter((b) => b.work_time_id === entry.id)
        .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
        .map((b) => ({ start_time: b.start_time, end_time: b.end_time }));
      if (rows.length > 0) {
        setWorkTimeForm((prev) => applyBreaks(prev, rows));
        setBreakEntriesMissing(false);
      } else {
        setBreakEntriesMissing((entry.break_minutes || 0) > 0);
      }
    } catch (error) {
      setBreakEntriesMissing(false);
    }
  };  
  
  // Check if end_time < start_time (night shift)
  const isNextDayEnd = (start, end) => {
    if (!start || !end) return false;
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    return (endH * 60 + endM) < (startH * 60 + startM);
  };

  // Work Time Dialog
  const openWorkTimeDialog = (day, existingEntry = null) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    setSelectedDate(day);
    setRestPeriodWarning(null);
    
    if (existingEntry) {
      setEditingWorkTime(existingEntry);
      
      // For needs_review entries: leave end_time empty and focus it
      const isReviewEntry = existingEntry.needs_review === true;
      
      setWorkTimeForm({
        date: existingEntry.date,
        start_time: existingEntry.start_time || '08:00',
        end_time: isReviewEntry ? '' : (existingEntry.end_time || '16:30'),
        break_minutes: existingEntry.break_minutes ?? 30,
        hours: isReviewEntry ? 0 : existingEntry.hours,
        breaks: (existingEntry.break_minutes ?? 0) > 0
          ? [{ start_time: '12:00', end_time: fromMinutes(720 + existingEntry.break_minutes) }]
          : [],
      });
      setBreakEntriesMissing(false);
      loadBreaksForEntry(existingEntry);
      // Check rest period for existing entry (only if not a review entry)
      if (!isReviewEntry) {
        checkRestPeriod(existingEntry.date, existingEntry.start_time, existingEntry.end_time);
      }
      
      // Focus end_time field after dialog opens for review entries
      if (isReviewEntry) {
        setTimeout(() => {
          const endTimeInput = document.querySelector('input[type="time"][data-testid="end-time-input"]');
          if (endTimeInput) endTimeInput.focus();
        }, 100);
      }
    } else {
      setEditingWorkTime(null);
      setWorkTimeForm({
        date: dateStr,
        start_time: '08:00',
        end_time: '16:30',
        break_minutes: 30,
        hours: 8,
        breaks: [{ start_time: '12:00', end_time: '12:30' }],
      });
      setBreakEntriesMissing(false);
      // Check rest period for new entry
      checkRestPeriod(dateStr, '08:00', '16:30');
    }
    setWorkTimeDialogOpen(true);
  };

  // Project Time Dialog
  const openProjectTimeDialog = (day, existingEntry = null) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    setSelectedDate(day);
    setCategoryValid(true); // Reset validity on dialog open
    
    if (existingEntry) {
      setEditingProjectTime(existingEntry);
      // Find the customer for this project
      const project = projects.find(p => p.id === existingEntry.project_id);
      setSelectedCustomerId(project?.customer_id || '');
      setProjectTimeForm({
        project_id: existingEntry.project_id,
        date: existingEntry.date,
        hours: existingEntry.hours.toString(),
        description: existingEntry.description || '',
        is_daily_rate: existingEntry.is_daily_rate || false,
        category_id: existingEntry.category_id || '',
        activity_id: existingEntry.activity_id || '',
        billable_override: existingEntry.billable_override ?? null,
      });
    } else {
      setEditingProjectTime(null);
      setSelectedCustomerId('');
      setProjectTimeForm({
        project_id: '',
        date: dateStr,
        hours: '',
        description: '',
        is_daily_rate: false,
        category_id: '',
        activity_id: '',
        billable_override: null,
      });
    }
    setProjectTimeDialogOpen(true);
  };
  
  // Check rest period when work time form changes
  const checkRestPeriod = useCallback(async (date, startTime, endTime) => {
    if (!date || !startTime || !endTime) {
      setRestPeriodWarning(null);
      return;
    }
    
    try {
      const response = await api.get(`/break-compliance/${date}`);
      const restPeriod = response.data?.rest_period;
      
      if (restPeriod && (restPeriod.violation_before || restPeriod.violation_after)) {
        setRestPeriodWarning({
          violation_before: restPeriod.violation_before,
          violation_after: restPeriod.violation_after,
          rest_before_hours: restPeriod.rest_before_hours,
          rest_after_hours: restPeriod.rest_after_hours,
          required_hours: restPeriod.required_hours,
          messages: restPeriod.messages
        });
      } else {
        setRestPeriodWarning(null);
      }
    } catch (error) {
      // Ignore errors - this is just a preview
      setRestPeriodWarning(null);
    }
  }, [api]);

  // Save Work Time
  const handleSaveWorkTime = async (e) => {
    e.preventDefault();
    const wasApproved = isWeekApproved;
    try {
      const payload = {
        ...workTimeForm,
        hours: parseFloat(workTimeForm.hours),
      };
      // Pausenzeiten nur mitsenden, wenn Beginn und Ende der Arbeitszeit feststehen
      if (!payload.start_time || !payload.end_time) {
        delete payload.breaks;
      }
      
      // Add reason if editing and reason_required
      if (editingWorkTime && (weekStatus?.reason_required || showReasonField) && editReason.trim()) {
        payload.reason = editReason.trim();
      }

      let response;
      if (editingWorkTime) {
        response = await api.put(`/work-time/${editingWorkTime.id}`, payload);
        toast.success('Arbeitszeit aktualisiert');
      } else {
        response = await api.post('/work-time', payload);
        toast.success('Arbeitszeit erfasst');
        // If week was approved, notify about resubmission
        if (wasApproved) {
          toast.info('Woche erneut zur Genehmigung gestellt');
          window.dispatchEvent(new Event('ctime:approvals-changed'));
        }
      }

      // Check for break compliance warning
      if (response.data?.break_warning) {
        const warning = response.data.break_warning;
        toast.warning(
          `⚠️ Pausenwarnung: ${warning.message}`,
          { duration: 8000 }
        );
      }
      
      // Check for rest period warning (§ 5 ArbZG)
      if (response.data?.rest_period_warning) {
        const warning = response.data.rest_period_warning;
        const messages = warning.messages || [];
        messages.forEach(msg => {
          toast.warning(`⏰ ${msg}`, { duration: 10000 });
        });
      }
      
      // Check for Sunday work hint (§ 11 ArbZG)
      if (response.data?.sunday_work_hint) {
        setSundayWorkHint(response.data.sunday_work_hint);
        toast.info(
          `☀️ ${response.data.sunday_work_hint.message}`,
          { duration: 10000 }
        );
      } else {
        setSundayWorkHint(null);
      }

      setWorkTimeDialogOpen(false);
      setRestPeriodWarning(null);
      setEditReason('');
      setShowReasonField(false);
      fetchWeekData();
      fetchWeekStatus();
      // Refresh review entries after saving
      fetchClockStatusForReviews();
    } catch (error) {
      // Check for reason_required error
      if (isReasonRequiredError(error)) {
        setShowReasonField(true);
        toast.warning(getErrorMessage(error, 'Änderungsgrund erforderlich'));
        return; // Keep dialog open
      }
      
      const status = error.response?.status;
      if (status === 423) {
        toast.warning(getErrorMessage(error, 'Eintrag gesperrt'));
      } else {
        toast.error(getErrorMessage(error, 'Fehler beim Speichern'));
      }
    }
  };

  // Save Project Time
  const handleSaveProjectTime = async (e) => {
    e.preventDefault();
    if (isSavingProjectTime) return;
    setIsSavingProjectTime(true);
    try {
      const payload = {
        ...projectTimeForm,
        hours: parseFloat(projectTimeForm.hours),
        category_id: projectTimeForm.category_id || null,
        activity_id: projectTimeForm.activity_id || null,
        billable_override: projectTimeForm.billable_override,
      };
      
      // Add reason if editing and reason_required
      if (editingProjectTime && (weekStatus?.reason_required || showReasonField) && editReason.trim()) {
        payload.reason = editReason.trim();
      }

      if (editingProjectTime) {
        await api.put(`/project-time/${editingProjectTime.id}`, payload);
        toast.success('Projektzeit aktualisiert');
      } else {
        await api.post('/project-time', payload);
        toast.success('Projektzeit gebucht');
      }

      setProjectTimeDialogOpen(false);
      setEditReason('');
      setShowReasonField(false);
      fetchWeekData();
      fetchWeekStatus();
    } catch (error) {
      // Check for reason_required error
      if (isReasonRequiredError(error)) {
        setShowReasonField(true);
        toast.warning(getErrorMessage(error, 'Änderungsgrund erforderlich'));
        setIsSavingProjectTime(false);
        return; // Keep dialog open
      }
      if (error.response?.status === 423) {
        toast.warning(getErrorMessage(error, 'Eintrag gesperrt'));
      } else {
        toast.error(getErrorMessage(error, 'Fehler beim Speichern'));
      }
    } finally {
      setIsSavingProjectTime(false);
    }
  };

  // Delete entries with GoBD confirmation
  const handleDeleteWorkTime = useCallback((entryId) => {
    requestDelete({
      title: "Arbeitszeit löschen",
      itemDescription: "diesen Arbeitszeit-Eintrag",
      requireReason: weekStatus?.reason_required === true,
      onConfirm: async (reason) => {
        setWeekData(prev => ({
          ...prev,
          work_time: prev.work_time.filter(entry => entry.id !== entryId)
        }));
        
        try {
          await api.delete(`/work-time/${entryId}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`);
          toast.success('Arbeitszeit gelöscht');
          fetchWeekStatus();
        } catch (error) {
          if (isReasonRequiredError(error)) {
            toast.warning(getErrorMessage(error, 'Fehler beim Löschen'));
          } else {
            toast.error(getErrorMessage(error, 'Fehler beim Löschen'));
          }
          fetchWeekData();
        }
      }
    });
  }, [api, fetchWeekData, fetchWeekStatus, requestDelete, weekStatus]);

  const handleDeleteProjectTime = useCallback((entryId) => {
    requestDelete({
      title: "Projektzeit löschen",
      itemDescription: "diesen Projektzeit-Eintrag",
      requireReason: weekStatus?.reason_required === true,
      onConfirm: async (reason) => {
        setWeekData(prev => ({
          ...prev,
          project_time: prev.project_time.filter(entry => entry.id !== entryId)
        }));
        
        try {
          await api.delete(`/project-time/${entryId}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`);
          toast.success('Projektzeit gelöscht');
          fetchWeekStatus();
        } catch (error) {
          if (isReasonRequiredError(error)) {
            toast.warning(getErrorMessage(error, 'Fehler beim Löschen'));
          } else {
            toast.error(getErrorMessage(error, 'Fehler beim Löschen'));
          }
          fetchWeekData();
        }
      }
    });
  }, [api, fetchWeekData, fetchWeekStatus, requestDelete, weekStatus]);
  
  // Show audit history for an entry
  const showHistory = (collection, id) => {
    setHistoryEntry({ collection, id });
    setHistoryPanelOpen(true);
  };

  // Submit week for approval
  const handleSubmitWeek = async () => {
    try {
      const response = await api.post(`/approvals/submit?week_start=${format(weekStart, 'yyyy-MM-dd')}`);
      // Show info messages (non-blocking hints) if present
      if (response.data.info && response.data.info.length > 0) {
        toast.info(`Eingereicht - Hinweise: ${response.data.info.join(', ')}`);
      } else {
        toast.success('Woche zur Genehmigung eingereicht');
      }
      fetchWeekData();
      fetchWeekStatus();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Fehler beim Einreichen'));
    }
  };

  // Dispute an approved week with admin edits
  const handleDispute = async () => {
    if (!weekData.approval?.id) return;
    try {
      await api.post(`/approvals/${weekData.approval.id}/dispute`, {
        reason: disputeReason || undefined,
      });
      toast.success('Genehmigung wurde angefochten. Sie können die Woche nun erneut bearbeiten.');
      setDisputeDialogOpen(false);
      setDisputeReason('');
      fetchWeekData();
      fetchWeekStatus();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Fehler beim Anfechten'));
    }
  };

  // Withdraw a pending submission
  const handleWithdraw = async () => {
    if (!weekData.approval?.id) return;
    try {
      await api.post(`/approvals/${weekData.approval.id}/withdraw`);
      toast.success('Einreichung zurückgezogen');
      setWithdrawDialogOpen(false);
      fetchWeekData();
      fetchWeekStatus();
      window.dispatchEvent(new Event('ctime:approvals-changed'));
    } catch (error) {
      const status = error.response?.status;
      if (status === 409) {
        toast.warning(getErrorMessage(error, 'Fehler beim Zurückziehen'));
        fetchWeekData();
        fetchWeekStatus();
      } else {
        toast.error(getErrorMessage(error, 'Fehler beim Zurückziehen'));
      }
    }
  };

  // Request day unlock for approved week
  const handleDayUnlock = async () => {
    if (!dayUnlockDate || dayUnlockReason.trim().length < 10) return;
    try {
      await api.post('/locks/unlock-requests', {
        scope: 'day',
        date: format(dayUnlockDate, 'yyyy-MM-dd'),
        reason: dayUnlockReason.trim(),
      });
      toast.success('Entsperrung beantragt');
      setDayUnlockDialogOpen(false);
      setDayUnlockDate(null);
      setDayUnlockReason('');
      fetchWeekStatus();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Fehler beim Beantragen der Entsperrung'));
    }
  };

  // Open day unlock dialog
  const openDayUnlockDialog = (day) => {
    setDayUnlockDate(day);
    setDayUnlockReason('');
    setDayUnlockDialogOpen(true);
  };

  // Helper functions - Support multiple work time entries per day
  const getWorkTimesForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return weekData.work_time?.filter(w => w.date === dateStr) || [];
  };

  const getWorkHoursForDay = (day) => {
    return getWorkTimesForDay(day).reduce((sum, w) => sum + (w.hours || 0), 0);
  };

  const getProjectTimeForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return weekData.project_time?.filter(p => p.date === dateStr) || [];
  };

  const getTotalWorkHours = () => weekData.work_time?.reduce((sum, w) => sum + w.hours, 0) || 0;
  const getTotalProjectHours = () => weekData.project_time?.reduce((sum, p) => sum + p.hours, 0) || 0;

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'Unbekannt';
  };

  const isHoliday = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return weekData.holidays?.[dateStr];
  };

  const isVacation = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return weekData.vacations?.some(v => dateStr >= v.start_date && dateStr <= v.end_date);
  };

  const getLeaveForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return weekData.vacations?.find(v => dateStr >= v.start_date && dateStr <= v.end_date);
  };

  const getLeaveLabel = (leave) => {
    if (!leave) return null;
    const leaveType = leave.leave_type || 'vacation';
    if (leaveType === 'sick') return { label: 'Krank', color: 'bg-red-500/20 text-red-400' };
    if (leaveType === 'special_leave') return { label: 'Sonderurlaub', color: 'bg-amber-500/20 text-amber-400' };
    return { label: 'Urlaub', color: 'bg-blue-500/20 text-blue-400' };
  };

  const isWeekApproved = weekData.approval?.status === 'approved';
  const isWeekPending = weekData.approval?.status === 'pending';
  const isWeekRejected = weekData.approval?.status === 'rejected';
  const isWeekFixedOrFinal = weekData.approval?.status === 'fixed_by_supervisor' || weekData.approval?.status === 'final';
  const canEdit = !isWeekApproved && !isWeekPending && !isWeekFixedOrFinal; // Rejected weeks CAN be edited
  const isPartialUnlock = weekStatus?.partial_unlock === true;
  const unlockedDates = weekStatus?.unlocked_dates || [];
  const isDayLocked = (day) => isPartialUnlock && !unlockedDates.includes(format(day, 'yyyy-MM-dd'));
  const showDayUnlockButton = (day) => !isWeekFixedOrFinal && weekStatus?.can_request_day_unlock && !weekStatus?.open_unlock_request && (isWeekApproved || isDayLocked(day));
  const hasAdminEdits = (weekData.approval?.admin_edits || []).length > 0;
  
  // Index admin edits by entry_id for quick lookup
  const adminEditsByEntryId = {};
  (weekData.approval?.admin_edits || []).forEach(edit => {
    adminEditsByEntryId[edit.entry_id] = edit;
  });

  // Break compliance check (German labor law - ArbZG)
  const getBreakWarning = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const workHours = getWorkHoursForDay(day);
    
    // Get break minutes from work entries (break_minutes field)
    const dayEntries = weekData.work_time?.filter(e => e.date === dateStr) || [];
    const enteredBreakMinutes = dayEntries.reduce((sum, e) => sum + (e.break_minutes || 0), 0);

    // Lücken zwischen Arbeitsblöcken ab 15 Min. zählen als Ruhepause (B9, wie break_service.py)
    const blocks = dayEntries
      .map(e => {
        const s = toMinutes(e.start_time);
        let en = toMinutes(e.end_time);
        if (s === null || en === null) return null;
        if (en <= s) en += 1440; // Ende vor Beginn = Folgetag
        return [s, en];
      })
      .filter(Boolean)
      .sort((a, b) => a[0] - b[0]);
    let gapBreakMinutes = 0;
    let lastEnd = null;
    blocks.forEach(([s, en]) => {
      if (lastEnd !== null && s - lastEnd >= MIN_QUALIFYING_BREAK) gapBreakMinutes += s - lastEnd;
      lastEnd = lastEnd === null ? en : Math.max(lastEnd, en);
    });
    const breakMinutes = enteredBreakMinutes + gapBreakMinutes;
    
    // German labor law requirements
    if (workHours > 9 && breakMinutes < 45) {
      return {
        type: 'error',
        message: `Mind. 45 Min Pause erforderlich (>9h). Aktuell: ${breakMinutes} Min.`,
        missing: 45 - breakMinutes
      };
    } else if (workHours > 6 && breakMinutes < 30) {
      return {
        type: 'warning',
        message: `Mind. 30 Min Pause erforderlich (>6h). Aktuell: ${breakMinutes} Min.`,
        missing: 30 - breakMinutes
      };
    }
    
    // Check if work exceeds 10h limit
    if (workHours > 10) {
      return {
        type: 'error',
        message: `Arbeitszeit überschreitet 10h (ArbZG § 3)`,
        exceeds: workHours - 10
      };
    }
    
    return null;
  };
  
  // Get rest period warning for a day
  const getRestPeriodWarning = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const dayEntries = weekData.work_time?.filter(e => e.date === dateStr) || [];
    
    // Check each entry for rest period compliance info
    for (const entry of dayEntries) {
      if (entry.rest_period_compliance && !entry.rest_period_compliance.is_compliant) {
        const rpc = entry.rest_period_compliance;
        if (rpc.violation_before || rpc.violation_after) {
          return {
            type: 'warning',
            violation_before: rpc.violation_before,
            violation_after: rpc.violation_after,
            messages: rpc.messages || ['Ruhezeitverletzung nach § 5 ArbZG']
          };
        }
      }
    }
    
    return null;
  };

  const getStatusBadge = () => {
    if (isWeekApproved) return <span className="badge badge-approved"><Check className="w-3 h-3 mr-1" />Genehmigt</span>;
    if (isWeekPending) return <span className="badge badge-pending"><Clock className="w-3 h-3 mr-1" />Ausstehend</span>;
    if (isWeekRejected) return <span className="badge badge-rejected"><X className="w-3 h-3 mr-1" />Abgelehnt</span>;
    if (isWeekFixedOrFinal) return <span className="badge bg-amber-500/15 text-amber-800 dark:text-amber-300 border border-amber-500/30" data-testid="week-fixed-badge"><Shield className="w-3 h-3 mr-1" />{weekData.approval.status === 'final' ? 'Abgeschlossen' : 'Festgesetzt'}</span>;
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="time-tracking-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Zeiterfassung</h1>
          <p className="text-muted-foreground mt-1">Arbeitszeit & Projektzeit erfassen</p>
        </div>
        {canEdit && (
          <Button
            onClick={() => openProjectTimeDialog(new Date())}
            data-testid="add-project-time-button"
            className="bg-indigo-500 hover:bg-indigo-600 text-white glow-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Projektzeit buchen
          </Button>
        )}
      </div>

      {/* Week Navigation */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card-glass p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={handlePrevWeek} data-testid="prev-week-button" className="text-muted-foreground hover:text-foreground hover:bg-accent">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="text-center min-w-[200px]">
              <p className="text-lg font-medium text-foreground">KW {format(weekStart, 'w', { locale: de })}</p>
              <p className="text-sm text-muted-foreground">{format(weekStart, 'd. MMM', { locale: de })} - {format(weekEnd, 'd. MMM yyyy', { locale: de })}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleNextWeek} data-testid="next-week-button" className="text-muted-foreground hover:text-foreground hover:bg-accent">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex items-center gap-3">
            {getStatusBadge()}
            <Button variant="outline" onClick={handleThisWeek} className="border-border text-muted-foreground hover:bg-accent">Diese Woche</Button>
            {canEdit && (weekData.work_time?.length > 0 || weekData.project_time?.length > 0) && (
              <Button onClick={handleSubmitWeek} data-testid="submit-week-button" className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30">
                <Send className="w-4 h-4 mr-2" />Woche einreichen
              </Button>
            )}
          </div>
        </div>
        
        {/* Last unlocked hint - shown when week was unlocked and no active approval */}
        {weekStatus?.last_unlocked && !weekData.approval?.status && (
          <div className="mt-4 p-3 rounded-lg bg-indigo-500/15 border border-indigo-500/30">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-indigo-800 dark:text-indigo-300 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-indigo-800 dark:text-indigo-300">
                {isPartialUnlock ? (
                  <>Entsperrt: {unlockedDates.map(d => new Date(d + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })).join(', ')}. Andere Tage bleiben gesperrt. Bitte nach den Änderungen die Woche erneut einreichen.</>
                ) : (
                  <>Diese Woche wurde am {new Date(weekStatus.last_unlocked.unlocked_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' })} zur Bearbeitung freigegeben ({weekStatus.last_unlocked.unlock_reason}). Bitte nach den Änderungen erneut einreichen.</>
                )}
              </span>
            </div>
          </div>
        )}

        {/* Week totals */}
        <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-4">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground flex items-center gap-2"><Briefcase className="w-4 h-4" />Arbeitszeit:</span>
            <span className="text-xl font-mono font-bold text-foreground">{formatHoursToHM(getTotalWorkHours())}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground flex items-center gap-2"><FolderKanban className="w-4 h-4" />Projektzeit:</span>
            <span className="text-xl font-mono font-bold text-lime-600 dark:text-lime-400">{formatHoursToHM(getTotalProjectHours())}</span>
          </div>
        </div>
        
        {/* Average Working Time Status (§ 3 Satz 2 ArbZG) */}
        <div className="mt-4 pt-4 border-t border-border">
          <AverageWorkTimeStatus 
            referenceDate={format(weekEnd, 'yyyy-MM-dd')}
            showDetails={false}
          />
        </div>
      </motion.div>

      {/* Pending Week Notice with Withdraw Button and Reopen Info */}
      {isWeekPending && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-lg bg-indigo-500/15 border border-indigo-500/30"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
              <Clock className="w-4 h-4 text-indigo-800 dark:text-indigo-300" />
            </div>
            <div className="flex-1">
              <h3 className="text-indigo-800 dark:text-indigo-300 font-medium mb-1">Woche eingereicht</h3>
              {weekData.approval?.reopen_reason === 'entry_added_after_approval' && weekData.approval?.previously_approved_at && (
                <p className="text-sm text-indigo-800 dark:text-indigo-300 mb-2">
                  Diese Woche war bereits genehmigt (am {new Date(weekData.approval.previously_approved_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' })}) und wurde nach einer nachträglichen Buchung erneut zur Genehmigung gestellt.
                </p>
              )}
              {weekData.approval?.reopen_reason === 'unlock_request' && (
                <p className="text-sm text-indigo-800 dark:text-indigo-300 mb-2">
                  Nach der Entsperrung erneut zur Genehmigung gestellt.
                </p>
              )}
              {!weekData.approval?.reopen_reason && weekData.approval?.changed_after_submit_at && (
                <p className="text-sm text-indigo-800 dark:text-indigo-300 mb-2">
                  Nach der Einreichung geändert am {new Date(weekData.approval.changed_after_submit_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Berlin' })}, {new Date(weekData.approval.changed_after_submit_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' })}. Die Änderung ist für die Genehmigung sichtbar.
                </p>
              )}
              {!weekData.approval?.reopen_reason && !weekData.approval?.changed_after_submit_at && (
                <p className="text-sm text-muted-foreground">
                  Die Woche wartet auf Genehmigung durch Ihren Vorgesetzten.
                </p>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setWithdrawDialogOpen(true)}
              data-testid="withdraw-button"
              className="border-indigo-500/30 text-indigo-800 dark:text-indigo-300 hover:bg-indigo-500/20 flex-shrink-0"
            >
              <Undo2 className="w-4 h-4 mr-2" />
              Einreichung zurückziehen
            </Button>
          </div>
        </motion.div>
      )}

      {/* Approved Week Notice - Hinweis auf automatische Neueinreichung */}
      {isWeekApproved && !hasAdminEdits && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-lg bg-emerald-500/15 border border-emerald-500/30"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Check className="w-4 h-4 text-emerald-800 dark:text-emerald-300" />
            </div>
            <div className="flex-1">
              <h3 className="text-emerald-800 dark:text-emerald-300 font-medium mb-1">Woche genehmigt</h3>
              <p className="text-sm text-muted-foreground">
                Nachträgliche Buchungen stellen die Woche automatisch erneut zur Genehmigung.
              </p>
              {/* Open unlock request hint */}
              {weekStatus?.open_unlock_request && (
                <p className="text-sm text-indigo-800 dark:text-indigo-300 mt-2">
                  Entsperrung für den {new Date(weekStatus.open_unlock_request.date + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Berlin' })}. beantragt am {new Date(weekStatus.open_unlock_request.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Berlin' })}. – wartet auf Genehmigung.
                </p>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Rest Period Violations Overview (Admin/Supervisor only) */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <RestPeriodViolationsList
          startDate={format(weekStart, 'yyyy-MM-dd')}
          endDate={format(weekEnd, 'yyyy-MM-dd')}
        />
      </motion.div>

      {/* Rejection Notice - shown when week was rejected */}
      {isWeekRejected && weekData.approval?.rejection_reason && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-lg bg-red-500/10 border border-red-500/30"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-red-600 dark:text-red-400 font-medium mb-1">Woche wurde abgelehnt</h3>
              <p className="text-sm text-muted-foreground mb-3">{weekData.approval.rejection_reason}</p>
              <p className="text-sm text-muted-foreground">Bitte korrigieren Sie die Zeiteinträge und reichen Sie die Woche erneut ein.</p>
            </div>
            <Button
              onClick={handleSubmitWeek}
              data-testid="resubmit-week-button"
              className="bg-emerald-500 hover:bg-emerald-600 text-white flex-shrink-0"
            >
              <Send className="w-4 h-4 mr-2" />
              Erneut einreichen
            </Button>
          </div>
        </motion.div>
      )}

      {/* Admin Edits Notice - shown when week was approved with modifications */}
      {isWeekApproved && hasAdminEdits && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="admin-edits-notice"
          className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-amber-600 dark:text-amber-400 font-medium mb-1">Arbeitszeit wurde korrigiert</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Ihre Arbeitszeiten wurden vor der Genehmigung angepasst. Details:
              </p>
              <div className="space-y-2">
                {weekData.approval.admin_edits.map((edit) => (
                  <div key={edit.id} className="p-2 rounded bg-muted/50 border border-border">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">{edit.entry_date}:</span>
                      <span className="text-red-600 dark:text-red-400 line-through">
                        {edit.original_values.start_time}-{edit.original_values.end_time} ({formatHoursToHM(Number(edit.original_values.hours))})
                      </span>
                      <span className="text-muted-foreground">→</span>
                      <span className="text-emerald-600 dark:text-emerald-400">
                        {edit.new_values.start_time}-{edit.new_values.end_time} ({formatHoursToHM(Number(edit.new_values.hours))})
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 italic">&ldquo;{edit.comment}&rdquo;</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground mt-3">
                Falls ein Fehler vorliegt, können Sie die Genehmigung anfechten. 
                Der Workflow wird dann neu gestartet.
              </p>
            </div>
            <Button
              onClick={() => { setDisputeReason(''); setDisputeDialogOpen(true); }}
              data-testid="dispute-button"
              className="bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 flex-shrink-0"
            >
              <ShieldAlert className="w-4 h-4 mr-2" />
              Anfechten
            </Button>
          </div>
        </motion.div>
      )}

      {/* Review Entries Banner - fehlende Endzeiten */}
      {reviewEntries.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="review-entries-banner"
          className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-4 h-4 text-amber-800 dark:text-amber-300" />
            </div>
            <div className="flex-1">
              {reviewEntries.length === 1 ? (
                <div>
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    Am {new Date(reviewEntries[0].date + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}. wurde nicht ausgestempelt (Beginn {reviewEntries[0].start_time}). Bitte Endzeit nachtragen.
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-amber-800 dark:text-amber-300 mb-2">
                    An {reviewEntries.length} Tagen fehlt die Endzeit:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {reviewEntries.map((entry) => (
                      <button
                        key={entry.id}
                        type="button"
                        onClick={() => {
                          const entryDate = new Date(entry.date + 'T00:00:00');
                          setCurrentDate(startOfWeek(entryDate, { weekStartsOn: 1 }));
                          setEntryToOpen(entry.id);
                        }}
                        className="text-sm text-amber-800 dark:text-amber-300 underline hover:no-underline"
                      >
                        {new Date(entry.date + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}.
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {reviewEntries.length === 1 && (
              <Button
                type="button"
                onClick={() => {
                  const entry = reviewEntries[0];
                  const entryDate = new Date(entry.date + 'T00:00:00');
                  setCurrentDate(startOfWeek(entryDate, { weekStartsOn: 1 }));
                  setEntryToOpen(entry.id);
                }}
                className="bg-amber-500/20 text-amber-800 dark:text-amber-300 border border-amber-500/30 hover:bg-amber-500/30 flex-shrink-0"
              >
                Nachtragen
              </Button>
            )}
          </div>
        </motion.div>
      )}

      {/* Fixed / final week banner */}
      {isWeekFixedOrFinal && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <FixedWeekBanner
            approval={weekData.approval}
            onObject={async (reason) => {
              await api.post(`/approvals/${weekData.approval.id}/object`, { reason });
              toast.success('Einspruch eingelegt – die Geschäftsführung wurde informiert');
              fetchWeekData();
              fetchWeekStatus();
            }}
          />
        </motion.div>
      )}

      {/* Week Grid */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4">
        {weekDays.map((day, index) => {
          const workTimeEntries = getWorkTimesForDay(day);
          const projectEntries = getProjectTimeForDay(day);
          const isToday = isSameDay(day, new Date());
          const holiday = isHoliday(day);
          const vacation = isVacation(day);
          const leaveInfo = getLeaveForDay(day);
          const leaveLabel = getLeaveLabel(leaveInfo);
          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
          const cardWidthClass = isWeekend ? 'sm:basis-[140px] sm:grow' : 'sm:basis-[240px] sm:grow-[2]';
          const totalProjectHours = projectEntries.reduce((sum, p) => sum + p.hours, 0);
          const totalWorkHours = getWorkHoursForDay(day);
          const breakWarning = getBreakWarning(day);
          const restWarning = getRestPeriodWarning(day);

          // Check if project time < work time (warning)
          const projectHoursNonDaily = projectEntries.filter(p => !p.is_daily_rate).reduce((sum, p) => sum + p.hours, 0);
          const hasWarning = totalWorkHours > 0 && projectHoursNonDaily < totalWorkHours;

          return (
            <motion.div
              key={day.toString()}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`card-glass p-4 ${cardWidthClass} ${isToday ? 'ring-1 ring-indigo-500/50' : ''} ${holiday || vacation ? 'bg-amber-500/5' : ''} ${isWeekend ? 'border-l-2 border-l-orange-500/50' : ''} ${breakWarning?.type === 'error' ? 'ring-1 ring-red-500/50' : ''}`}
            >
              {/* Day header */}
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">{format(day, 'EEEE', { locale: de })}</p>
                  <p className={`text-lg font-medium ${isToday ? 'text-indigo-600 dark:text-indigo-400' : 'text-foreground'}`}>{format(day, 'd. MMM', { locale: de })}</p>
                </div>
                <div className="flex items-center gap-2">
                  {breakWarning && (
                    <div className={breakWarning.type === 'error' ? 'text-red-400' : 'text-amber-600 dark:text-amber-400'} title={breakWarning.message}>
                      <AlertTriangle className="w-4 h-4" />
                    </div>
                  )}
                  {restWarning && (
                    <RestPeriodIndicator
                      violationBefore={restWarning.violation_before}
                      violationAfter={restWarning.violation_after}
                    />
                  )}
                  {hasWarning && (
                    <div className="text-amber-600 dark:text-amber-400" title="Auftragszeit < Arbeitszeit">
                      <AlertTriangle className="w-4 h-4" />
                    </div>
                  )}
                  {/* Partial unlock: unlocked day badge */}
                  {isPartialUnlock && !isDayLocked(day) && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border border-indigo-500/30" data-testid={`day-unlocked-badge-${format(day, 'yyyy-MM-dd')}`}>
                      Entsperrt
                    </span>
                  )}
                  {/* Day unlock button for approved weeks / locked days in partially unlocked weeks */}
                  {showDayUnlockButton(day) && (
                    <button
                      type="button"
                      onClick={() => openDayUnlockDialog(day)}
                      className="p-1 rounded text-indigo-600 dark:text-indigo-400 hover:bg-indigo-500/20 flex items-center gap-1"
                      title="Tag zur Bearbeitung entsperren"
                      data-testid={`day-unlock-${format(day, 'yyyy-MM-dd')}`}
                    >
                      <Lock className="w-4 h-4" />
                      {isDayLocked(day) && <span className="text-[10px]">Tag entsperren</span>}
                    </button>
                  )}
                </div>
              </div>

              {/* Holiday/Vacation/Sick badge */}
              {holiday && <div className="mb-2 px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded">{holiday}</div>}
              {leaveLabel && <div className={`mb-2 px-2 py-1 ${leaveLabel.color} text-xs rounded`}>{leaveLabel.label}</div>}
              
              {/* Break compliance warning */}
              {breakWarning && (
                <div className={`mb-2 px-2 py-1 text-xs rounded ${breakWarning.type === 'error' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                  ⚠️ {breakWarning.message}
                </div>
              )}
              
              {/* Rest period warning (§ 5 ArbZG) */}
              {restWarning && (
                <div className="mb-2 px-2 py-1 text-xs rounded bg-amber-500/20 text-amber-400">
                  ⏰ {restWarning.messages?.[0] || 'Ruhezeitverletzung nach § 5 ArbZG'}
                </div>
              )}
              
              {/* Sunday work indicator (§ 11 ArbZG) */}
              {day.getDay() === 0 && workTimeEntries.length > 0 && (
                <div className="mb-2 px-2 py-1 text-xs rounded bg-amber-500/20 text-amber-400 flex items-center gap-1" data-testid={`sunday-work-indicator-${format(day, 'yyyy-MM-dd')}`}>
                  ☀️ Sonntagsarbeit – Ersatzruhetag nach § 11 ArbZG erforderlich
                </div>
              )}

              {/* Work Time Section - Now supports multiple entries */}
              <div className="mb-3 p-2 rounded-lg bg-muted/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground uppercase">Arbeitszeit</span>
                  {canEdit && !holiday && !isDayLocked(day) && (
                    <button type="button" onClick={() => openWorkTimeDialog(day, null)} className="text-muted-foreground hover:text-foreground">
                      <Plus className="w-3 h-3" />
                    </button>
                  )}
                </div>
                
                {workTimeEntries.length > 0 ? (
                  <div className="space-y-1">
                    {workTimeEntries.map((entry) => {
                      const entryEdit = adminEditsByEntryId[entry.id];
                      const lockInfo = getLockStatus('work_time', entry.id);
                      const entryEditable = canEditEntry('work_time', entry);
                      const isStorno = entry.is_correction;
                      const isNightShift = isNextDayEnd(entry.start_time, entry.end_time);
                      const needsReview = entry.needs_review === true;
                      
                      // Format time display
                      let timeDisplay;
                      if (needsReview) {
                        // For needs_review entries, show only "ab HH:MM" instead of "HH:MM - HH:MM"
                        timeDisplay = `ab ${entry.start_time}`;
                      } else if (isNightShift) {
                        // For night shifts, show "22:00 – 03:00 (+1)"
                        timeDisplay = `${entry.start_time} – ${entry.end_time} (+1)`;
                      } else {
                        timeDisplay = `${entry.start_time} - ${entry.end_time}`;
                      }
                      
                      return (
                      <div key={entry.id} className={`flex flex-col ${entryEdit ? 'p-1.5 rounded bg-amber-500/10 border border-amber-500/20' : ''} ${isStorno ? 'opacity-60 line-through' : ''}`}>
                        <div className="flex items-center justify-between group">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm text-muted-foreground">{timeDisplay}</span>
                            <LockBadge lockInfo={lockInfo} size="sm" />
                            {isStorno && <StornoBadge entry={entry} />}
                            {needsReview && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-amber-500/15 text-amber-800 dark:text-amber-300">
                                <AlertTriangle className="w-3 h-3" />
                                Endzeit fehlt
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-mono text-foreground">{formatHoursToHM(Number(entry.hours))}</span>
                            {entryEditable && canEdit && (
                              <div className="flex ml-1">
                                <button 
                                  type="button"
                                  onClick={() => openWorkTimeDialog(day, entry)} 
                                  className={`p-0.5 ${needsReview ? 'text-amber-600 dark:text-amber-400 hover:text-amber-500' : 'text-muted-foreground hover:text-foreground'}`} 
                                  title="Bearbeiten"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                                <button 
                                  type="button"
                                  onClick={() => handleDeleteWorkTime(entry.id)} 
                                  data-testid={`delete-work-time-${entry.id}`}
                                  className="p-0.5 text-muted-foreground hover:text-red-600 dark:text-red-400" 
                                  title="Löschen"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                                <button 
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); showHistory('work_time', entry.id); }} 
                                  data-testid={`history-work-time-${entry.id}`}
                                  className="p-0.5 text-muted-foreground hover:text-indigo-600 dark:text-indigo-400" 
                                  title="Änderungshistorie"
                                >
                                  <History className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                            {/* Locked entry actions - hidden for approved weeks (use day unlock instead) */}
                            {!entryEditable && lockInfo.level === 'hard' && lockInfo.can_request_unlock && !isWeekApproved && (
                              <button
                                type="button"
                                onClick={() => openUnlockDialog([entry], 'work_time')}
                                className="p-0.5 text-amber-600 dark:text-amber-400 hover:text-amber-300"
                                title="Entsperrung beantragen"
                                data-testid={`unlock-work-time-${entry.id}`}
                              >
                                <Lock className="w-3 h-3" />
                              </button>
                            )}
                            {lockInfo.level === 'final' && (user?.role === 'admin' || user?.role === 'accounting') && (
                              <button
                                onClick={() => openCorrectionDialog(entry, 'work_time')}
                                className="p-0.5 text-red-600 dark:text-red-400 hover:text-red-300"
                                title="Storno erstellen"
                                data-testid={`storno-work-time-${entry.id}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                            {/* History always visible */}
                            {!entryEditable && (
                              <button 
                                onClick={(e) => { e.stopPropagation(); showHistory('work_time', entry.id); }} 
                                data-testid={`history-work-time-${entry.id}`}
                                className="p-0.5 text-muted-foreground hover:text-indigo-600 dark:text-indigo-400" 
                                title="Änderungshistorie"
                              >
                                <History className="w-3 h-3" />
                              </button>
                            )}
                        </div>
                      </div>
                        {(entry.break_periods || []).length > 0 && (
                          <p className="text-[10px] leading-tight text-muted-foreground mt-0.5">
                            {`Pause${entry.break_periods.length > 1 ? 'n' : ''} ${entry.break_periods
                              .map((b) => `${b.start_time}–${b.end_time}`)
                              .join(', ')}`}
                          </p>
                        )}
                        {(entry.break_periods || []).length === 0 && (entry.break_minutes || 0) > 0 && (
                          <p className="text-[10px] leading-tight text-amber-600 dark:text-amber-400 mt-0.5">
                            Pause {entry.break_minutes} Min. – Zeiten fehlen
                          </p>
                        )}                      
                        {entryEdit && (
                          <div className="mt-0.5">
                            <p className="text-[10px] text-amber-600 dark:text-amber-400">
                              Korrigiert (war: {entryEdit.original_values.start_time}-{entryEdit.original_values.end_time}, {formatHoursToHM(Number(entryEdit.original_values.hours))})
                            </p>
                            <p className="text-[10px] text-muted-foreground italic">&ldquo;{entryEdit.comment}&rdquo;</p>
                          </div>
                        )}
                      </div>
                      );
                    })}
                    {/* Day total if multiple entries */}
                    {workTimeEntries.length > 1 && (
                      <div className="pt-1 mt-1 border-t border-border flex justify-end">
                        <span className="text-xs text-muted-foreground">Gesamt: <span className="font-mono font-bold text-foreground">{formatHoursToHM(totalWorkHours)}</span></span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">-</p>
                )}
              </div>

              {/* Project Time Section */}
              <div className="p-2 rounded-lg bg-indigo-500/5 border border-indigo-500/10">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground uppercase">Projektzeit</span>
                  {canEdit && !isDayLocked(day) && (
                    <button 
                      type="button"
                      onClick={() => openProjectTimeDialog(day)} 
                      data-testid={`add-project-time-${format(day, 'yyyy-MM-dd')}`}
                      className="p-1 rounded text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/20"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                <div className="space-y-1 min-h-[40px]">
                  {projectEntries.map((entry) => {
                    const lockInfo = getLockStatus('project_time', entry.id);
                    const entryEditable = canEditEntry('project_time', entry);
                    const isStorno = entry.is_correction;
                    // Resolve billable - use entry's category if set, otherwise project default
                    const entryCategory = flatCategories.find(c => c.id === entry.category_id);
                    const isBillable = entry.billable_override !== null && entry.billable_override !== undefined
                      ? entry.billable_override
                      : entryCategory?.billable_default ?? true;
                    
                    return (
                    <div key={entry.id} className={`flex items-center justify-between p-1.5 rounded bg-muted/50 group ${isStorno ? 'opacity-60 line-through' : ''} ${!isBillable ? 'opacity-70' : ''}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 flex-wrap">
                          <p className="text-xs font-medium text-foreground break-words leading-tight">{getProjectName(entry.project_id)}</p>
                          {entry.is_daily_rate && <span className="text-xs text-amber-600 dark:text-amber-400">TP</span>}
                          <LockBadge lockInfo={lockInfo} size="sm" />
                          {isStorno && <StornoBadge entry={entry} />}
                          {/* Category Tag */}
                          {entry.category_id && (
                            <CategoryTag categoryId={entry.category_id} categories={flatCategories} small />
                          )}
                          {/* Billable indicator */}
                          <BillableIndicator billable={isBillable} small />
                          {/* Surcharge indicator */}
                          {entry.surcharge_info?.has_surcharge && (
                            <span 
                              className="px-1 py-0.5 rounded text-[10px] bg-amber-500/20 text-amber-400 border border-amber-500/30"
                              title={`${entry.surcharge_info.surcharge_display} (+${entry.surcharge_info.surcharge_percent}%)`}
                            >
                              +{entry.surcharge_info.surcharge_percent}%
                            </span>
                          )}
                        </div>
                        {/* Admin: Show surcharge breakdown */}
                        {user.role === 'admin' && entry.surcharge_info?.has_surcharge && (
                          <p className="text-[10px] text-amber-600 dark:text-amber-400/80 mt-0.5">
                            {entry.hours}h × {entry.surcharge_info.factor?.toFixed(2)} = {(entry.hours * (entry.surcharge_info.factor || 1)).toFixed(2)}h
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-mono text-lime-600 dark:text-lime-400 ml-2 whitespace-nowrap">{formatHoursToHM(Number(entry.hours))}</span>
                        {entryEditable && canEdit && (
                          <div className="flex ml-1">
                            <button onClick={() => openProjectTimeDialog(day, entry)} className="p-0.5 text-muted-foreground hover:text-foreground" title="Bearbeiten">
                              <Edit2 className="w-3 h-3" />
                            </button>
                            <button 
                              onClick={() => handleDeleteProjectTime(entry.id)} 
                              data-testid={`delete-project-time-${entry.id}`}
                              className="p-0.5 text-muted-foreground hover:text-red-600 dark:text-red-400" 
                              title="Löschen"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                            <button 
                              onClick={(e) => { e.stopPropagation(); showHistory('project_time', entry.id); }} 
                              data-testid={`history-project-time-${entry.id}`}
                              className="p-0.5 text-muted-foreground hover:text-indigo-600 dark:text-indigo-400" 
                              title="Änderungshistorie"
                            >
                              <History className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                        {/* Locked entry actions - hidden for approved weeks (use day unlock instead) */}
                        {!entryEditable && lockInfo.level === 'hard' && lockInfo.can_request_unlock && !isWeekApproved && (
                          <button
                            type="button"
                            onClick={() => openUnlockDialog([entry], 'project_time')}
                            className="p-0.5 text-amber-600 dark:text-amber-400 hover:text-amber-300"
                            title="Entsperrung beantragen"
                            data-testid={`unlock-project-time-${entry.id}`}
                          >
                            <Lock className="w-3 h-3" />
                          </button>
                        )}
                        {lockInfo.level === 'final' && (user?.role === 'admin' || user?.role === 'accounting') && (
                          <button
                            onClick={() => openCorrectionDialog(entry, 'project_time')}
                            className="p-0.5 text-red-600 dark:text-red-400 hover:text-red-300"
                            title="Storno erstellen"
                            data-testid={`storno-project-time-${entry.id}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                        {/* History always visible */}
                        {!entryEditable && (
                          <button 
                            onClick={(e) => { e.stopPropagation(); showHistory('project_time', entry.id); }} 
                            data-testid={`history-project-time-${entry.id}`}
                            className="p-0.5 text-muted-foreground hover:text-indigo-600 dark:text-indigo-400" 
                            title="Änderungshistorie"
                          >
                            <History className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                  })}
                  {projectEntries.length === 0 && <p className="text-xs text-muted-foreground">Keine Buchungen</p>}
                </div>

                {projectEntries.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border flex justify-end">
                    <span className="text-sm font-mono font-bold text-lime-600 dark:text-lime-400">{formatHoursToHM(Number(totalProjectHours))}</span>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Work Time Dialog */}
      <Dialog open={workTimeDialogOpen} onOpenChange={setWorkTimeDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">{editingWorkTime ? 'Arbeitszeit bearbeiten' : 'Arbeitszeit erfassen'}</DialogTitle>
          </DialogHeader>
          
          {/* Review entry hint */}
          {editingWorkTime?.needs_review && (
            <div className="p-3 bg-amber-500/15 border border-amber-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-800 dark:text-amber-300 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-amber-800 dark:text-amber-300">
                  Ausstempeln fehlte – bitte die tatsächliche Endzeit eintragen.
                </span>
              </div>
            </div>
          )}
          
          {/* Approved week hint - only for new entries */}
          {!editingWorkTime && isWeekApproved && (
            <div className="p-3 bg-amber-500/15 border border-amber-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-amber-800 dark:text-amber-300 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-amber-800 dark:text-amber-300">
                  Diese Woche ist bereits genehmigt. Mit dem Speichern wird sie erneut zur Genehmigung gestellt.
                </span>
              </div>
            </div>
          )}

          <form onSubmit={handleSaveWorkTime} className="space-y-4 min-w-0">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Beginn</Label>
                <Input
                  type="time"
                  value={workTimeForm.start_time}
                  onChange={(e) => {
                    setWorkTimeForm(applyBreaks({ ...workTimeForm, start_time: e.target.value }, workTimeForm.breaks));
                  }}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Ende</Label>
                <Input
                  type="time"
                  data-testid="end-time-input"
                  value={workTimeForm.end_time}
                  onChange={(e) => {
                    setWorkTimeForm(applyBreaks({ ...workTimeForm, end_time: e.target.value }, workTimeForm.breaks));
                  }}
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>
            
            {/* Night shift hint: Ende am Folgetag */}
            {workTimeForm.start_time && workTimeForm.end_time && isNextDayEnd(workTimeForm.start_time, workTimeForm.end_time) && (
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Ende am Folgetag
              </div>
            )}

            {/* Pausen (§ 4 ArbZG) - Beginn und Ende sind nachzuweisen */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-muted-foreground">Pausen</Label>
                <span className="text-xs text-muted-foreground">gesamt {workTimeForm.break_minutes} Min.</span>
              </div>

              {(workTimeForm.breaks || []).length === 0 && (
                <p className="text-xs text-muted-foreground">Keine Pause erfasst.</p>
              )}

              {(workTimeForm.breaks || []).map((br, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={br.start_time}
                    onChange={(e) => {
                      const next = [...workTimeForm.breaks];
                      next[index] = { ...next[index], start_time: e.target.value };
                      setWorkTimeForm(applyBreaks(workTimeForm, next));
                    }}
                    className="bg-input border-border text-foreground"
                  />
                  <span className="text-muted-foreground">–</span>
                  <Input
                    type="time"
                    value={br.end_time}
                    onChange={(e) => {
                      const next = [...workTimeForm.breaks];
                      next[index] = { ...next[index], end_time: e.target.value };
                      setWorkTimeForm(applyBreaks(workTimeForm, next));
                    }}
                    className="bg-input border-border text-foreground"
                  />
                  <span className="w-14 text-right text-xs text-muted-foreground">
                    {breakDuration(br, workTimeForm.start_time)} Min.
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    title="Pause entfernen"
                    onClick={() => setWorkTimeForm(applyBreaks(workTimeForm, (workTimeForm.breaks || []).filter((_, i) => i !== index)))}
                    className="px-2 text-muted-foreground hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}

              <Button
                type="button"
                variant="ghost"
                onClick={() => setWorkTimeForm(applyBreaks(workTimeForm, [...(workTimeForm.breaks || []), suggestNextBreak(workTimeForm)]))}
                className="w-full justify-start text-indigo-600 dark:text-indigo-400 hover:text-indigo-500"
              >
                <Plus className="w-4 h-4 mr-1" />
                Pause hinzufügen
              </Button>

              {breakEntriesMissing && (
                <div className="flex items-start gap-2 p-2 text-xs rounded bg-amber-500/15 text-amber-800 dark:text-amber-300">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    Für diesen Eintrag sind bisher nur Pausenminuten ohne Zeiten hinterlegt. Bitte Beginn und Ende
                    nachtragen.
                  </span>
                </div>
              )}

              {getBreakRuleHints(workTimeForm).map((hint, i) => (
                <div key={i} className="flex items-start gap-2 p-2 text-xs rounded bg-amber-500/15 text-amber-800 dark:text-amber-300">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>{hint}</span>
                </div>
              ))}
            </div>

            <div className="p-4 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Berechnete Arbeitszeit:</span>
                <span className="text-2xl font-mono font-bold text-indigo-600 dark:text-indigo-400">{formatHoursToHM(Number(workTimeForm.hours))}</span>
              </div>
            </div>
            
            {/* Rest Period Warning Preview */}
            {restPeriodWarning && <RestPeriodWarning warning={restPeriodWarning} />}
            
            {/* Average Working Time Impact Warning */}
            <AverageImpactWarning 
              date={selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null} 
              hours={workTimeForm.hours} 
            />

            {/* Change reason field - only when editing and reason required */}
            {editingWorkTime && (weekStatus?.reason_required || showReasonField) && (
              <div className="space-y-2">
                <Label className={`text-muted-foreground ${showReasonField ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                  Änderungsgrund <span className="text-red-600 dark:text-red-400">*</span>
                </Label>
                <Textarea
                  data-testid="edit-reason-input"
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  className={`bg-input border-border text-foreground resize-none ${showReasonField && editReason.trim().length < 5 ? 'border-amber-500' : ''}`}
                  placeholder="Warum wird der Eintrag geändert? (mind. 5 Zeichen)"
                  rows={2}
                  required
                  minLength={5}
                />
                {editReason.trim().length > 0 && editReason.trim().length < 5 && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">Mindestens 5 Zeichen erforderlich</p>
                )}
              </div>
            )}

            {/* Sunday Work Hint */}
            {selectedDate && selectedDate.getDay() === 0 && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg mt-2">
                <div className="flex items-start gap-2">
                  <span className="text-amber-600 dark:text-amber-400 text-lg">☀️</span>
                  <div className="flex-1">
                    <div className="text-sm text-amber-600 dark:text-amber-400">
                      Sonntagsarbeit: Nach § 11 ArbZG ist ein Ersatzruhetag innerhalb von zwei Wochen zu gewähren.
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button type="button" variant="ghost" onClick={() => setWorkTimeDialogOpen(false)} className="text-muted-foreground hover:text-foreground">Abbrechen</Button>
              {editingWorkTime && (
                <Button type="button" onClick={() => { handleDeleteWorkTime(editingWorkTime.id); setWorkTimeDialogOpen(false); }} className="bg-red-500/20 text-red-400 hover:bg-red-500/30">Löschen</Button>
              )}
              <Button type="submit" className="bg-indigo-500 hover:bg-indigo-600 text-foreground">Speichern</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Project Time Dialog */}
      <Dialog open={projectTimeDialogOpen} onOpenChange={setProjectTimeDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">{editingProjectTime ? 'Projektzeit bearbeiten' : 'Projektzeit buchen'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSaveProjectTime} className="space-y-4 min-w-0">
            {/* Customer Selection (required first) */}
            <div className="space-y-2">
              <Label className="text-muted-foreground">Kunde <span className="text-red-600 dark:text-red-400">*</span></Label>
              <SearchableSelect
                options={customers.map(customer => ({
                  value: customer.id,
                  label: customer.name
                }))}
                value={selectedCustomerId || ''}
                onValueChange={(value) => {
                  setSelectedCustomerId(value);
                  setProjectTimeForm({ ...projectTimeForm, project_id: '' });
                }}
                placeholder="Bitte zuerst Kunde auswählen"
                searchPlaceholder="Kunde suchen..."
                emptyText="Keine Kunden gefunden"
                dataTestId="customer-select"
              />
            </div>

            {/* Project Selection (filtered by customer, disabled until customer selected) */}
            <div className="space-y-2">
              <Label className="text-muted-foreground">Auftrag <span className="text-red-600 dark:text-red-400">*</span></Label>
              <SearchableSelect
                options={filteredProjects.map(project => ({
                  value: project.id,
                  label: project.name
                }))}
                value={projectTimeForm.project_id}
                onValueChange={(value) => setProjectTimeForm({ ...projectTimeForm, project_id: value })}
                disabled={!selectedCustomerId}
                placeholder={selectedCustomerId ? "Auftrag auswählen" : "Erst Kunde auswählen"}
                searchPlaceholder="Auftrag suchen..."
                emptyText="Keine Aufträge gefunden"
                dataTestId="project-select"
              />
              {selectedCustomerId && filteredProjects.length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400">Keine Aufträge für diesen Kunden vorhanden</p>
              )}
              {/* Live Rate Display */}
              {projectTimeForm.project_id && selectedDate && (
                <LiveRateDisplay
                  projectId={projectTimeForm.project_id}
                  date={selectedDate instanceof Date ? format(selectedDate, 'yyyy-MM-dd') : selectedDate}
                  rateType={projectTimeForm.is_daily_rate ? 'daily' : 'hourly'}
                />
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Stunden</Label>
              <Input
                type="number"
                step="0.25"
                min="0.25"
                max="24"
                data-testid="hours-input"
                value={projectTimeForm.hours}
                onChange={(e) => setProjectTimeForm({ ...projectTimeForm, hours: e.target.value })}
                required
                className="bg-input border-border text-foreground"
                placeholder="z.B. 1.5"
              />
              <p className="text-xs text-muted-foreground">In 15-Minuten-Intervallen (0.25h)</p>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Beschreibung (optional)</Label>
              <Textarea
                data-testid="description-input"
                value={projectTimeForm.description}
                onChange={(e) => setProjectTimeForm({ ...projectTimeForm, description: e.target.value })}
                className="bg-input border-border text-foreground resize-none"
                placeholder="Was haben Sie gemacht?"
                rows={3}
              />
            </div>

            {/* Category Selection */}
            <CategorySelector
              value={projectTimeForm.category_id}
              onChange={(catId) => setProjectTimeForm({ ...projectTimeForm, category_id: catId })}
              projectId={projectTimeForm.project_id}
              billableOverride={projectTimeForm.billable_override}
              onBillableOverrideChange={(val) => setProjectTimeForm({ ...projectTimeForm, billable_override: val })}
              showBillableSwitch={false}
              onValidityChange={setCategoryValid}
              disabled={!projectTimeForm.project_id}
              existingEntryCategory={editingProjectTime?.category_id}
              activityId={projectTimeForm.activity_id}
              onActivityChange={handleActivityChange}
            />

            {/* Tagespauschale bei der Buchung entfernt – Abrechnung als Pauschale gehört an den Auftrag (L8) */}

            {/* Change reason field - only when editing and reason required */}
            {editingProjectTime && (weekStatus?.reason_required || showReasonField) && (
              <div className="space-y-2">
                <Label className={`text-muted-foreground ${showReasonField ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                  Änderungsgrund <span className="text-red-600 dark:text-red-400">*</span>
                </Label>
                <Textarea
                  data-testid="project-edit-reason-input"
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  className={`bg-input border-border text-foreground resize-none ${showReasonField && editReason.trim().length < 5 ? 'border-amber-500' : ''}`}
                  placeholder="Warum wird der Eintrag geändert? (mind. 5 Zeichen)"
                  rows={2}
                  required
                  minLength={5}
                />
                {editReason.trim().length > 0 && editReason.trim().length < 5 && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">Mindestens 5 Zeichen erforderlich</p>
                )}
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button type="button" variant="ghost" onClick={() => { setProjectTimeDialogOpen(false); setCategoryValid(true); }} className="text-muted-foreground hover:text-foreground">Abbrechen</Button>
              <Button type="submit" data-testid="save-entry-button" disabled={isSavingProjectTime || !categoryValid} className="bg-indigo-500 hover:bg-indigo-600 text-foreground">{isSavingProjectTime ? 'Speichern…' : (editingProjectTime ? 'Speichern' : 'Buchen')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dispute Dialog */}
      <Dialog open={disputeDialogOpen} onOpenChange={setDisputeDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">Genehmigung anfechten</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 mb-2">
                <ShieldAlert className="w-4 h-4" />
                <span className="text-sm font-medium">Hinweis</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Wenn Sie die Genehmigung anfechten, werden alle Admin-Änderungen rückgängig gemacht
                und der Genehmigungsworkflow für diese Woche wird komplett neu gestartet.
                Sie müssen die Woche anschließend erneut einreichen.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Begründung (optional)</Label>
              <Textarea
                data-testid="dispute-reason-input"
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                className="bg-input border-border text-foreground resize-none"
                placeholder="Warum ist die Korrektur nicht korrekt?"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDisputeDialogOpen(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleDispute}
              data-testid="confirm-dispute-button"
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              <ShieldAlert className="w-4 h-4 mr-2" />
              Anfechten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GoBD Delete Confirmation Dialog */}
      <GoBDDeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={deleteConfig.title}
        itemDescription={deleteConfig.itemDescription}
        requireReason={deleteConfig.requireReason}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Audit History Panel Dialog */}
      <Dialog open={historyPanelOpen} onOpenChange={setHistoryPanelOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <History className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              Änderungshistorie
            </DialogTitle>
          </DialogHeader>
          {historyEntry.id && (
            <AuditHistoryPanel
              collectionName={historyEntry.collection}
              documentId={historyEntry.id}
              isOpen={true}
              compact={false}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Unlock Request Dialog */}
      <UnlockRequestDialog
        open={unlockDialogOpen}
        onOpenChange={setUnlockDialogOpen}
        entries={unlockEntries}
        collectionName={unlockCollection}
        onSuccess={() => {
          fetchWeekData();
          fetchLockStatuses(weekData.work_time || [], weekData.project_time || []);
        }}
      />

      {/* Correction/Storno Dialog */}
      <CorrectionDialog
        open={correctionDialogOpen}
        onOpenChange={setCorrectionDialogOpen}
        entry={correctionEntry}
        collectionName={correctionCollection}
        onSuccess={() => {
          fetchWeekData();
          fetchLockStatuses(weekData.work_time || [], weekData.project_time || []);
        }}
      />

      {/* Withdraw Confirmation Dialog */}
      <Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">Einreichung zurückziehen?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Die Woche gilt danach wieder als nicht eingereicht und kann bearbeitet und erneut eingereicht werden.
          </p>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => setWithdrawDialogOpen(false)} className="text-muted-foreground hover:text-foreground">
              Abbrechen
            </Button>
            <Button type="button" onClick={handleWithdraw} className="bg-indigo-500 hover:bg-indigo-600 text-white">
              Zurückziehen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Day Unlock Request Dialog */}
      <Dialog open={dayUnlockDialogOpen} onOpenChange={setDayUnlockDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading flex items-center gap-2">
              <Unlock className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              Tag entsperren
            </DialogTitle>
          </DialogHeader>
          
          {dayUnlockDate && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-muted/50 border border-border">
                <p className="text-sm text-foreground">
                  <span className="font-medium">Datum:</span> {format(dayUnlockDate, 'EEEE, d. MMMM yyyy', { locale: de })}
                </p>
              </div>

              <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-indigo-800 dark:text-indigo-300 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-indigo-800 dark:text-indigo-300">
                    Nach der Genehmigung wird die ganze Woche wieder bearbeitbar und muss neu eingereicht werden.
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">
                  Begründung <span className="text-red-600 dark:text-red-400">*</span>
                </Label>
                <Textarea
                  data-testid="day-unlock-reason-input"
                  value={dayUnlockReason}
                  onChange={(e) => setDayUnlockReason(e.target.value)}
                  className={`bg-input border-border text-foreground resize-none ${dayUnlockReason.trim().length > 0 && dayUnlockReason.trim().length < 10 ? 'border-amber-500' : ''}`}
                  placeholder="Warum soll der Tag entsperrt werden? (mind. 10 Zeichen)"
                  rows={3}
                  required
                  minLength={10}
                />
                {dayUnlockReason.trim().length > 0 && dayUnlockReason.trim().length < 10 && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">Mindestens 10 Zeichen erforderlich</p>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDayUnlockDialogOpen(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={handleDayUnlock}
              disabled={dayUnlockReason.trim().length < 10}
              data-testid="confirm-day-unlock-button"
              className="bg-indigo-500 hover:bg-indigo-600 text-white"
            >
              <Unlock className="w-4 h-4 mr-2" />
              Entsperrung beantragen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
